import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Type,
  ViewChildren
} from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CustomHostDirective,
  FilterListLocationConfigService,
  FilterListRoutingService,
  GungListRouterEventsService,
  ListItemRendererComponent,
  ListLayout,
  ListLayoutComponent,
  Pagination,
  PimTemplateProperties
} from 'gung-list';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  first,
  forkJoin,
  fromEventPattern,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';
import { GungBackendFilterListService } from '../../services/gung-backend-filter-list.service';
import { FilterSelected, GungBackendFilterListConfigService, GungBackendRenderFilter } from '../../models/gung-backend-filter-list';
import { User } from '../../state/auth/types';
import { GungFlow } from '../../state/flow/types';
import { AuthService } from '../../services/auth/auth.service';
import { GungFlowService } from '../../services/gung-flow/gung-flow.service';
import { ColumnSortSelectionService } from '../../services/column-sort-selection/column-sort-selection.service';

@Component({
  selector: 'lib-gung-backend-filter-list',
  templateUrl: './gung-backend-filter-list.component.html',
  styleUrl: './gung-backend-filter-list.component.scss',
  standalone: false
})
export class GungBackendFilterListComponent<T> implements OnInit, OnChanges, OnDestroy {
  public items: T[];
  public matchingIds: string[];
  public totalItemsNumber: number;
  public renderItemsSubject = new BehaviorSubject<T[]>([]);
  public renderItems: Observable<T[]> = this.renderItemsSubject.asObservable();
  public shouldDisplayOverlay: boolean;
  protected itemRendererComponent: Type<ListItemRendererComponent<T | T[]>>;
  protected currentQuery: any;
  protected scrollToPosition: boolean = true;

  public searches: Record<string, string> = {};
  public searchString: string;
  public searchDisabled: boolean;
  public searchAboveFilters = false;
  public allFilters = [];
  public renderFilters: GungBackendRenderFilter[];
  public renderFiltersForSelected: GungBackendRenderFilter[];
  public showFilterTitle: 'minimal' | 'group';

  public pagination: Pagination = { totalPages: 0, currentPage: 1 };
  public batchSizes: number[];
  public batchSize: number;
  public limit: number;
  public skip: number = 0;
  public defaultLayout: number;
  public currentLayoutIdx: number;
  public layouts: ListLayout<T>[];

  public isSmallScreen = false;
  protected maxScreenWidthValue = '991';
  protected getMediaQuerySubscription: Subscription;

  protected unsubscribe: Subject<void> = new Subject();
  protected unsubscribeRouterEvents = new Subject<void>();

  protected baseFilterClasses: { [className: string]: boolean } = { open: false };

  private loadingVar = true;

  set loading(value: boolean) {
    this.loadingVar = value;
    this.loaded.emit(!value);
  }

  get loading() {
    return this.loadingVar;
  }

  @ViewChildren(CustomHostDirective)
  protected viewChild: QueryList<CustomHostDirective>;

  @Input()
  public configService: GungBackendFilterListConfigService<T>;

  @Input()
  topFilter?: boolean;

  @Input()
  selectedFilterTop?: boolean;

  @Input()
  paginationList?: boolean;

  @Output()
  loaded = new EventEmitter<boolean>();

  currentUser: User;
  currentFlow: GungFlow;
  currentSelectedSort: PimTemplateProperties;
  loadingItemsState: boolean = false;

  constructor(
    protected filterListService: GungBackendFilterListService<T>,
    protected filterListLocationConfigService: FilterListLocationConfigService,
    public filterListRoutingService: FilterListRoutingService,
    protected gungListRouterEventsService: GungListRouterEventsService,
    public route: ActivatedRoute,
    public changeDetectorRef: ChangeDetectorRef,
    protected router: Router,
    protected location: Location,
    protected authService: AuthService,
    protected gungFlowService: GungFlowService,
    protected columnSortSelectionService: ColumnSortSelectionService
  ) {}

  ngOnInit(): void {
    this.batchSizes = this.configService.getBatchSizes() ? this.configService.getBatchSizes(this.route.snapshot) : [12];
    this.batchSize = this.batchSize || this.batchSizes[0];
    this.limit = this.limit || this.batchSizes[0];
    this.searchDisabled = !!this.configService.searchDisabled;
    this.showFilterTitle = this.configService.showFilterTitle;
    this.layouts = this.configService.getLayouts(this.route.snapshot);
    this.currentLayoutIdx = this.defaultLayout || 0;

    combineLatest({
      user: this.authService.getCurrentUser(),
      flow: this.gungFlowService.getSelectedFlow(),
    }).pipe(
      takeUntil(this.unsubscribe),
      switchMap(({ user, flow}) => forkJoin({
        user: of(user),
        flow: of(flow),
        backendFilters: this.filterListService.getInitializedFilters(this.configService, this.route.snapshot).pipe(first())
      }))
    ).subscribe(({ user, flow, backendFilters}) => {
      this.currentUser = user;
      this.currentFlow = flow;

      this.renderFilters = backendFilters;
      if (this.renderFilters) {
        const copiedFilters = JSON.parse(JSON.stringify(this.renderFilters));
        this.renderFiltersForSelected = copiedFilters;
      }
      this.getQueryParams();

      this.columnSortSelectionService.getSelectedColumnSort().pipe(takeUntil(this.unsubscribe)).subscribe(selectedSort => {
        this.currentSelectedSort = selectedSort;
        this.runGetItems().pipe(first()).subscribe(() => {
          if (!!this.configService.paginationList) {
            this.initializePaginationList();
          }

          // Update filters after getting params
          this.renderFiltersForSelected = structuredClone(this.renderFilters);
          this.filterListService.updateFilters('', this.renderFilters, this.matchingIds, false, false);

          const toRender = this.layouts[this.currentLayoutIdx];
          this.renderLayout(toRender.getListLayoutComponent(), toRender.getListItemComponent());
          this.loading = false;
        });
      });
    });
  }

  runGetItems(type?: string): Observable<void> {
    this.loadingItemsState = true;
    return this.configService.getItems(
      this.skip,
      this.limit,
      this.searchString,
      this.allFilters,
      this.currentUser,
      this.currentFlow,
      this.route.snapshot,
      this.currentSelectedSort
    ).pipe(
      first(),
      tap(items => {
        if (type === 'LoadMore') {
          this.items = [...this.items, ...items.entities];
        } else if (type ===  'LoadPage') {
          this.items = items.entities;
        } else {
          this.items = items.entities;
          this.matchingIds = items.matchingSearchIds;
          this.totalItemsNumber = this.matchingIds.length;
        }
      })
    );
  }

  ngOnChanges(): void {
    this.initializeTopFilter();
    this.checkScreenChanges();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.unsubscribeRouterEvents.next();
    this.unsubscribeRouterEvents.complete();
  }

  getQueryParams() {
    const queryParams = this.route.snapshot.queryParams;
    if (queryParams && Object.keys(queryParams).length > 0) {
      const dataParams = JSON.parse(queryParams['backendList']);
      if (dataParams.limit) {
        this.limit = Number(dataParams.limit);
      }
      if (dataParams.currentPage) {
        this.pagination.currentPage = Number(dataParams.currentPage);
        this.skip = (this.pagination.currentPage - 1) * this.limit;
      }
      if (dataParams.search) {
        const searchField = (this.configService.getSearchFields?.() || [this.configService.getDefaultSearchField()])[0];
        this.searchString = dataParams.search;
        const parsedSearchKey = searchField.getKey() || this.configService.getDefaultSearchField().getKey();
        this.searches[parsedSearchKey] = dataParams.search;
      }
      if (dataParams.filter && dataParams.filter.length > 0) {
        for (const paramFilter of dataParams.filter) {
          let filterSelected: FilterSelected;
          if (Array.isArray(paramFilter.value)) {
            filterSelected = {
              filterId: paramFilter.index,
              filterField: paramFilter.index,
              optionIdRange: paramFilter.value
            };
          } else {
            filterSelected = {
              filterId: paramFilter.index,
              filterField: paramFilter.index,
              optionId: paramFilter.value
            };
          }

          const filterToUpdate = this.renderFilters.find(filter => filter.id.includes(paramFilter.index));

          if (!!filterSelected.optionIdRange && !filterSelected.optionId) {
            if (filterToUpdate) {
              const valuesToSelect = filterToUpdate.valueList.filter(value => Number(value.valueId) >= Number(filterSelected.optionIdRange[0]) && Number(value.valueId) <= Number(filterSelected.optionIdRange[1]));
              for (const valueToSelect of valuesToSelect) {
                if (valueToSelect) {
                  valueToSelect.selected = true;
                }
              }
              filterSelected.backendFilterValues = valuesToSelect.map(v => v.valueName);
              filterToUpdate.minValue = Number(filterSelected.optionIdRange[0]);
              filterToUpdate.maxValue = Number(filterSelected.optionIdRange[1]);
            }
          } else {
            if (filterToUpdate) {
              const valueToUpdate = filterToUpdate.valueList.find(value => value.valueId === paramFilter.value);
              if (valueToUpdate) {
                valueToUpdate.selected = true;
              }
            }
          }

          this.allFilters.push(filterSelected);
        }
      }
    }
  }

  setQueryParams(type: 'limit' | 'currentPage' | 'filter' | 'search') {
    if (type === 'filter') {
      let filtersParams = [];
      for (const filter of this.allFilters) {
        if (!!filter.optionIdRange && !filter.optionId) {
          const filterparam = { index: filter.filterField, value: filter.optionIdRange };
          filtersParams.push(filterparam);
        } else {
          const filterparam = { index: filter.filterField, value: filter.optionId };
          filtersParams.push(filterparam);
        }
      }
      if (filtersParams.length > 0) {
        this.currentQuery = {
          ...this.currentQuery,
          filter: filtersParams
        };
      } else if (this.currentQuery?.filter) {
        delete this.currentQuery.filter;
      }
    } else if (type === 'limit') {
      if (this.limit > this.batchSize) {
        this.currentQuery = {
          ...this.currentQuery,
          limit: this.limit
        };
      }
    } else if (type === 'currentPage') {
      if (this.pagination.currentPage > 1) {
        this.currentQuery = {
          ...this.currentQuery,
          currentPage: this.pagination.currentPage
        };
      } else if(this.currentQuery?.currentPage) {
        delete this.currentQuery.currentPage;
      }
    } else {
      if (this.searchString) {
        this.currentQuery = {
          ...this.currentQuery,
          search: this.searchString
        };
      } else if (this.currentQuery?.search) {
        delete this.currentQuery.search;
      }
    }
    const listQuery = JSON.stringify(this.currentQuery);
    let queryParams = {};
    queryParams['backendList'] = listQuery;

    const urlTree = this.router.createUrlTree([], {
      relativeTo: null,
      queryParams: queryParams,
      preserveFragment: false
    });

    this.location.replaceState(urlTree.toString());
  }

  initializeTopFilter(): void {
    if (this.topFilter !== undefined) {
      return;
    } else if (this.configService.topFilter !== undefined) {
      this.topFilter = this.configService.topFilter;
    } else {
      this.topFilter = false;
    }

    if (this.selectedFilterTop !== undefined) {
      return;
    } else if (this.configService.selectedFilterTop !== undefined) {
      this.selectedFilterTop = this.configService.selectedFilterTop;
    } else {
      this.selectedFilterTop = false;
    }

    this.searchAboveFilters = this.configService.searchAboveFilters;
  }

  initializePaginationList(): void {
    this.paginationList = this.configService.paginationList;
    this.pagination.totalPages = Math.ceil(this.totalItemsNumber / this.batchSize);
    this.pagination.currentPage = this.pagination.currentPage || 1;
  }

  checkScreenChanges() {
    if (this.topFilter) {
      const mediaQuery = matchMedia(`screen and (max-width: ${this.maxScreenWidthValue}px)`);
      this.isSmallScreen = mediaQuery.matches;

      if (!this.getMediaQuerySubscription) {
        const mediaObserver = fromEventPattern<MediaQueryListEvent>(
          handler => mediaQuery.addEventListener('change', handler),
          handler => mediaQuery.removeEventListener('change', handler)
        );
        this.getMediaQuerySubscription = mediaObserver.pipe(takeUntil(this.unsubscribe)).subscribe(result => {
          this.isSmallScreen = result.matches;
          this.baseFilterClasses.open = false;
          this.shouldDisplayOverlay = false;
        });
      }
    }
  }

  renderLayout(
    layoutComponent: Type<ListLayoutComponent<T>>,
    itemComponent: Type<ListItemRendererComponent<T | T[]>>
  ): void {
    if (!this.viewChild || this.viewChild.length === 0) {
      return;
    }

    this.itemRendererComponent = itemComponent;
    this.changeDetectorRef.detectChanges();
    const containerRef = this.viewChild.last.viewContainerRef;
    containerRef.clear();
    const componentRef = containerRef.createComponent(layoutComponent);
    const typedComponent = componentRef.instance as ListLayoutComponent<T>;

    // List view
    typedComponent.listItemRenderer = itemComponent;
    typedComponent.renderItems = this.renderItems;
    typedComponent.itemsPerRow = this.configService.itemsPerRow ? 12 / this.configService.itemsPerRow : 3;
    typedComponent.renderFinished = new EventEmitter<void>();
    typedComponent.renderFinished
      .pipe(takeUntil(this.unsubscribeRouterEvents))
      .subscribe(() => this.scrollFilterList());

    // Search
    const searchFieldContainerRef = this.viewChild.find(vc => vc.name === 'searchFields')?.viewContainerRef;
    if (searchFieldContainerRef) {
      searchFieldContainerRef.clear();
      for (const searchField of this.configService.getSearchFields?.() || [
        this.configService.getDefaultSearchField()
      ]) {
        const searchCompRef = searchFieldContainerRef.createComponent(searchField.getComponent());
        searchCompRef.instance.searchUpdated.pipe(takeUntil(this.unsubscribe), debounceTime(400)).subscribe(evt => this.setSearch(evt, searchField.getKey()));
        searchCompRef.instance.placeholder = searchField.getPlaceHolder();
        searchCompRef.instance.initSearchTerm = this.searches[searchField.getKey()];
        searchCompRef.instance.classes =
          searchField.componentClass?.() || this.configService.getDefaultSearchField().componentClass?.();
      }
    }

    this.changeDetectorRef.detectChanges();
    if (this.items) {
      this.updateRenderItems();
    }
  }

  updateRenderItems(): void {
    this.renderItemsSubject.next(this.items);
    this.loadingItemsState = false;
  }

  setSearch(searchString: string, searchKey?: string) {
    const parsedSearchKey = searchKey || this.configService.getDefaultSearchField().getKey();
    if (this.searchString == searchString) {
      return;
    }

    if (!searchString) {
      delete this.searches[parsedSearchKey];
    } else {
      this.searches[parsedSearchKey] = searchString;
    }
    this.searchString = searchString;

    if (!!searchString && searchString.length < 3) {
      return;
    }

    this.runGetItems().pipe(first()).subscribe(() => {
      if (!!this.configService.paginationList) {
        this.pagination.currentPage = 1;
        this.initializePaginationList();
      }

      this.updateRenderItems();
      this.filterListService.updateFilters(undefined, this.renderFilters, this.matchingIds, false, true);
      this.setQueryParams('search');
    });
  }

  setBatchSize(desiredSize: number) {
    this.limit = desiredSize;
    this.batchSize = desiredSize;
    let hasSearch = !!this.searchString;

    this.runGetItems().pipe(first()).subscribe(() => {
      if (!!this.configService.paginationList) {
        this.pagination.currentPage = 1;
        this.initializePaginationList();
      }

      this.updateRenderItems();
      this.filterListService.updateFilters(undefined, this.renderFilters, this.matchingIds, false, hasSearch);
    });
  }

  changeLayout(index: number) {
    if (typeof this.configService.setPreviousLayout === 'function') {
      this.configService.setPreviousLayout(index);
      this.defaultLayout = index;
    }
    this.currentLayoutIdx = index;
    this.renderLayout(this.layouts[index].getListLayoutComponent(), this.layouts[index].getListItemComponent());
  }

  toggleFilters(): void {
    this.baseFilterClasses.open = !this.baseFilterClasses.open;
    this.shouldDisplayOverlay = !this.shouldDisplayOverlay;
  }

  getGungFilterClasses(): { [className: string]: boolean } {
    const defaultClasses = {
      'col-lg-3': this.renderFilters && this.renderFilters.length > 0 && !this.topFilter
    };

    return {
      ...this.baseFilterClasses,
      ...defaultClasses
    };
  }

  setMultipleSelection(filterId: string, selected: boolean, optionIdRange: string[]): void {
    const filterSelected = {
      filterId,
      filterField: filterId.match(/@(.*?)\?/)[1],
      optionIdRange,
      backendFilterValues: []
    };


    const filterToUpdate = this.renderFilters.find(filter => filter.id === filterId);
    if (filterToUpdate) {
      if (selected) {
        const valuesToSelect = filterToUpdate.valueList.filter(value => Number(value.valueId) >= Number(optionIdRange[0]) && Number(value.valueId) <= Number(optionIdRange[1]));
        for (const valueToSelect of valuesToSelect) {
          if (valueToSelect) {
            valueToSelect.selected = selected;
          }
        }

        filterSelected.backendFilterValues = valuesToSelect.map(v => v.valueName);
        const valuesToSelectIds = valuesToSelect.map(v => v.valueId);
        const valuesToDeselect = filterToUpdate.valueList.filter(value => value.selected && !valuesToSelectIds.includes(value.valueId))
        for (const valueToDeselect of valuesToDeselect) {
          if (valueToDeselect) {
            valueToDeselect.selected = false;
          }
        }

        filterToUpdate.minValue = Number(optionIdRange[0]);
        filterToUpdate.maxValue = Number(optionIdRange[1]);
      } else {
        const valuesToSelect = filterToUpdate.valueList.filter(value => optionIdRange.includes(value.valueId));
        for (const valueToSelect of valuesToSelect) {
          if (valueToSelect) {
            valueToSelect.selected = selected;
          }
        }
      }
    }

    if (selected) {
      const foundFilterIndex = this.allFilters.findIndex(f => f.filterId === filterId);
      if (foundFilterIndex >= 0) {
        this.allFilters[foundFilterIndex] = filterSelected;
      } else {
        this.allFilters.push(filterSelected);
      }
    } else {
      this.allFilters = this.allFilters.filter(filter => filter.filterId !== filterId);
    }

    let hasSearch = !!this.searchString;
    this.renderFiltersForSelected = structuredClone(this.renderFilters);

    this.runGetItems().pipe(first()).subscribe(() => {
      if (!!this.configService.paginationList) {
        this.pagination.currentPage = 1;
        this.initializePaginationList();
      }

      this.updateRenderItems();
      this.filterListService.updateFilters(filterId, this.renderFilters, this.matchingIds, false, hasSearch);
      this.setQueryParams('filter');
    });
  }

  setSelection(filterId: string, optionId: string, selected: boolean): void {
    const filterSelected = {
      filterId,
      filterField: filterId.match(/@(.*?)\?/)[1],
      optionId
    };

    if (selected) {
      const existsInFilters = this.allFilters.some(
        filter => filter.filterId === filterId && filter.optionId === optionId
      );
      if (!existsInFilters) {
        this.allFilters.push(filterSelected);
      }
    } else {
      this.allFilters = this.allFilters.filter(
        filter => !(filter.filterId === filterId && filter.optionId === optionId)
      );
    }

    const filterToUpdate = this.renderFilters.find(filter => filter.id === filterId);
    if (filterToUpdate) {
      const valueToUpdate = filterToUpdate.valueList.find(value => value.valueId === optionId);
      if (valueToUpdate) {
        valueToUpdate.selected = selected;
      }
    }

    let hasSearch = !!this.searchString;
    this.renderFiltersForSelected = structuredClone(this.renderFilters);

    this.runGetItems().pipe(first()).subscribe(() => {
      if (!!this.configService.paginationList) {
        this.pagination.currentPage = 1;
        this.initializePaginationList();
      }

      this.updateRenderItems();
      this.filterListService.updateFilters(filterId, this.renderFilters, this.matchingIds, false, hasSearch);
      this.setQueryParams('filter');
    });
  }

  clearSelection(): void {
    this.allFilters = [];

    for (const filterToUpdate of this.renderFilters) {
      for (const valueToUpdate of filterToUpdate.valueList) {
        valueToUpdate.selected = false;
      }
    }

    let hasSearch = !!this.searchString;
    this.renderFiltersForSelected = structuredClone(this.renderFilters);

    this.runGetItems().pipe(first()).subscribe(() => {
      if (!!this.configService.paginationList) {
        this.pagination.currentPage = 1;
        this.initializePaginationList();
      }

      this.updateRenderItems();
      this.filterListService.updateFilters(undefined, this.renderFilters, this.matchingIds, true, hasSearch);
      this.setQueryParams('filter');
    });
  }

  loadMore(): void {
    this.skip += this.batchSize;
    this.limit = this.skip + this.batchSize;

    this.runGetItems('LoadMore').pipe(first()).subscribe(() => {
      this.updateRenderItems();
      this.setQueryParams('limit');
    });
  }

  loadPage(page): void {
    this.pagination.currentPage = page;
    this.skip = (page - 1) * this.limit;

    this.runGetItems('LoadPage').pipe(first()).subscribe(() => {
      this.updateRenderItems();
      this.setQueryParams('currentPage');

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  }

  protected scrollFilterList(): void {
    if (this.items?.length > 0 && this.scrollToPosition) {
      this.gungListRouterEventsService
        .getRouterNavigationStart()
        .pipe(
          first(),
          switchMap(nav =>
            forkJoin({
              nav: of(nav),
              savedLocation: this.filterListLocationConfigService
                .getSavedLocation(nav?.url?.split('?')?.[0] || '')
                .pipe(first())
            })
          )
        )
        .subscribe(({ nav, savedLocation }) => {
          if (nav?.navigationTrigger === 'popstate' && savedLocation) {
            if (this.layouts[savedLocation.layout] && this.currentLayoutIdx !== savedLocation.layout) {
              this.changeLayout(savedLocation.layout);
            }

            window.scrollTo({ top: savedLocation.scrollY, behavior: 'smooth' });
            this.scrollToPosition = false;
          }
        });
    }
  }

  getNothingFoundTranslateTag(): string {
    if (!!this.configService.getNothingFoundTranslateTag) {
      return this.configService.getNothingFoundTranslateTag();
    } else {
      return 'NOTHING_FOUND_CONTACT_GUNG';
    }
  }
}
